(ns app.front.view.property.gis-import.session-common
  (:require [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.table :as ui-table]
            [app.front.util :refer [for-frag]]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ session-id update-session-evt]]
    {:db           (assoc-in db [::data ::session-id] session-id)
     :comm/command {:command    :property-import/import-session
                    :data       (util/mmap session-id)
                    :on-success [::fetch-session.success session-id update-session-evt]
                    :method     :get}}))

(rf/reg-event-db ::clear
  (fn [db _]
    (dissoc db ::data)))

(rf/reg-event-fx ::fetch-session.success
  (fn [_ [_ session-id update-session-evt status entity-db]]
    {:fx           (concat (when update-session-evt
                             [[:dispatch [update-session-evt session-id]]])
                           [[:dispatch [::property-dashboard-subs/fetch-temp-records session-id]]
                            [:dispatch [::entities/merge-entity-db status entity-db]]])
     :comm/command {:command    :property-import/get-gis-mapping
                    :method     :get
                    :data       (util/mmap session-id)
                    :on-success [::get-gis-mapping.success]}}))

(rf/reg-event-db ::get-gis-mapping.success
  (fn [db [_ _ mapping]]
    (assoc-in db [::data ::gis-mapping] mapping)))

(rf/reg-event-fx ::cancel-async-job
  (fn [_ [_ async-job-id]]
    {:comm/command {:command    :async-job-management/cancel-job
                    :data       {:id async-job-id}
                    :on-success [::entities/apply-changes]
                    :notify     {:success :async-job-ui/job-cancelled}}}))

(rf/reg-event-fx ::remove-import-session
  (fn [_ [_ session-id success-msg]]
    {:comm/command {:command    :property-import/remove-import-session
                    :data       (util/mmap session-id)
                    :on-success [::remove-import-session.success]
                    :notify     {:success success-msg}}}))

(rf/reg-event-fx ::remove-import-session.success
  (fn [_ [_ status changes]]
    {:dispatch   [::entities/apply-changes status changes]
     :route/push {:route :view/property-gis-import}}))

(rf/reg-event-db ::set-mapping-modal-open?
  (fn [db [_ open?]]
    (assoc-in db [::data ::mapping-modal-open?] open?)))

(defn- default-main [{:keys [records]}]
  (-> records first :property/integration-id))

(defn group-resolution
  "The import choice of a duplicate group: the main record and the records added as its additional addresses. The
   other records are skipped."
  [resolutions {:property/keys [id] :as group}]
  (get resolutions id {:main (default-main group)}))

(defn- save-group-resolution [db {:property/keys [id] :as group} {:keys [main additional]}]
  (let [session-id (get-in db [::data ::session-id])]
    {:fx [[:dispatch [:comm/command-monitored
                      {:command    :property-import/set-duplicate-resolution
                       :data       (if (and (= main (default-main group))
                                            (empty? additional))
                                     {:session-id session-id :property-id id}
                                     (p/assoc-if {:session-id session-id :property-id id :main main}
                                                 :additional (not-empty additional)))
                       :on-success [::entities/apply-changes]}]]]}))

(rf/reg-event-fx ::set-duplicate-main
  (fn [{:keys [db]} [_ group resolution integration-id]]
    (->> (-> resolution
             (assoc :main integration-id)
             (update :additional disj integration-id))
         (save-group-resolution db group))))

(rf/reg-event-fx ::set-duplicate-additional
  (fn [{:keys [db]} [_ group resolution integration-id additional?]]
    (->> (update resolution :additional (if additional? (fnil conj #{}) disj) integration-id)
         (save-group-resolution db group))))

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::session-id
  :<- [::data]
  :-> ::session-id)

(rf/reg-sub ::duplicate-resolutions
  :<- [::entities/context]
  :<- [::session-id]
  (fn [[ctx session-id]]
    (->> (entities/find-entities ctx {:entity/parent session-id
                                      :entity/type   :entity.type/property-import-duplicate-resolution})
         property-model/duplicate-resolutions-by-property-id)))

(rf/reg-sub ::gis-mapping
  :<- [::data]
  :-> ::gis-mapping)

(rf/reg-sub ::gis-mapping-no-attributes
  :<- [::gis-mapping]
  :<- [::property-tr]
  (fn [[mapping tr] _]
    (->> (dissoc mapping :attributes)
         (map (fn [[k v]]
                [(tr [k]) v]))
         (sort-by first))))

(rf/reg-sub ::property-attribute-gis-mapping
  :<- [::gis-mapping]
  :<- [:language]
  (fn [[mapping lang] _]
    (->> (:attributes mapping)
         (map (fn [[k v]]
                [(i18n-model/get-text-from-entities-or-map k lang) v]))
         (sort-by first))))

(rf/reg-sub ::mapping-modal-open?
  :<- [::data]
  :-> ::mapping-modal-open?)

(rf/reg-sub ::session
  :<- [::session-id]
  :<- [::entities/context]
  (fn [[session-id ctx] _]
    (entities/find-entity ctx session-id)))

(rf/reg-sub ::async-jobs
  :<- [::entities/context]
  :<- [::session-id]
  (fn [[ctx session-id]]
    (->> (entities/find-entities ctx {:entity/parent session-id
                                      :entity/type   :entity.type/async-job})
         (sort-by :entity/created <))))

(rf/reg-sub ::property-tr
  :<- [::session]
  :<- [:tr]
  (fn [[session tr] _]
    (->> (:entity/parent session)
         (property-model/property-tr-fn tr))))

(defn jobs-table [{:keys [tr async-jobs]}]
  [eb/error-boundary
   (let [format-date (<sub [::date-util/date-time-fn])]
     [ui-table/modern-responsive-table
      {:tr           tr
       :items        async-jobs
       :id-func      :db/id
       :get-status   (fn [{:domain/keys [state]}]
                       (case state
                         :async-job.state/running :warning
                         :async-job.state/ready :ok
                         :async-job.state/failed :critical
                         nil))
       :make-actions (fn [job]
                       (if (du/in-state? :async-job.state/running job)
                         [{:title           (tr [:cancel])
                           :icon            i/stop-filled
                           :confirm?        true
                           :confirm-content (tr [:async-job-ui/cancel-job-confirm])
                           :on-click        #(>evt [::cancel-async-job (:db/id job)])}]
                         []))
       :cols         [[:async-job/task
                       (comp tr vector :async-job/task)]
                      [:entity/created
                       (comp format-date :entity/created)]
                      [:domain/state
                       (comp tr vector :domain/state)]
                      [:async-job/processed-count
                       :async-job/processed-count]
                      [:async-job/total-count
                       :async-job/total-count]]}])])

(defn- gis-fields [tr {:keys [attributes]}]
  [:div
   [:h5.mb-4 (tr [:property-gis-import/gis-fields])]
   [:div.grid.grid-cols-3.xl:grid-cols-5.gap-y-6
    (for-frag [[k v] attributes]
      [:div
       [:div.bx--label.break-all k]
       [:div.break-all v]])]])

(defn temp-records-table [{:keys [tr temp-records]}]
  [:<>
   [:h3.mb-4 (tr [:property-gis-import/available-records])]
   (when (seq temp-records)
     [:div.mb-4.text-xs (tr [:property-gis-import/first-n-displayed] ["100"])])
   [ui-table/modern-responsive-table
    {:tr                tr
     :items             temp-records
     :empty-placeholder (tr [:property-gis-import/no-records])
     :id-func           :property/integration-id
     :cols              [[:property/id
                          :property/id]
                         [:property/integration-id
                          :property/integration-id]
                         [:address/full-address
                          :address/full-address]]
     :expanded-func     (partial gis-fields tr)}]])

(defn- compare-record-count [a b]
  (compare (:record-count a) (:record-count b)))

(defn- record-addresses [{:keys [records]}]
  (->> records
       (keep :address/full-address)
       distinct
       (str/join "; ")))

(defn- record-label [{:address/keys [full-address] :property/keys [integration-id]}]
  (if full-address
    (str full-address " (" integration-id ")")
    integration-id))

(defn- main-record-select [tr disabled? {:property/keys [id] :keys [records] :as group} {:keys [main] :as resolution}]
  [ui-select/select {:id           (str "duplicate-main-record-" id)
                     :class        "full-width-select"
                     :label-text   (tr [:property-gis-import/main-record])
                     :hide-label   true
                     :allow-empty? false
                     :disabled     disabled?
                     :value        (p/find-first records #(= main (:property/integration-id %)))
                     :values       records
                     :val->str     record-label
                     :on-change    #(>evt [::set-duplicate-main group resolution (:property/integration-id %)])}])

(defn- record-import-choice [{:keys [tr multiple-addresses? disabled?]}
                             group
                             {:keys [main additional] :as resolution}
                             {:property/keys [integration-id]}]
  (cond
    (= main integration-id)
    (tr [:property-gis-import/main-record])

    multiple-addresses?
    (cc/checkbox {:id         (str "duplicate-additional-" (:property/id group) "-" integration-id)
                  :label-text (tr [:property-gis-import/add-as-additional-address])
                  :checked    (contains? additional integration-id)
                  :disabled   disabled?
                  :on-change  #(>evt [::set-duplicate-additional group resolution integration-id %])})

    :else
    (tr [:property-gis-import/not-imported])))

(defn duplicate-records-table [{:keys [tr duplicates disabled?]}]
  (let [resolutions         (<sub [::duplicate-resolutions])
        multiple-addresses? (<sub [::ff/enabled? :feature-flag.id/property-multiple-addresses])
        disabled?           (or disabled?
                                (<sub [:comm/command-processing? :property-import/set-duplicate-resolution]))]
    [sortable-table/table
     {:tr            tr
      :items         duplicates
      :id-func       :property/id
      :cols          (concat [[:property/id
                               :property/id
                               {:sorting true :width "12rem"}]
                              [:property-gis-import/record-count
                               :record-count
                               {:sorting compare-record-count :width "8rem"}]
                              [:property-gis-import/main-record
                               #(main-record-select tr disabled? % (group-resolution resolutions %))
                               {:flex 2}]]
                             (when multiple-addresses?
                               [[:property-gis-import/additional-address-count
                                 #(count (:additional (group-resolution resolutions %)))
                                 {:width "8rem"}]])
                             [[:address/full-address
                               record-addresses
                               {:truncate? true :flex 2}]])
      :expanded-func (fn [{:keys [records] :as group}]
                       (let [resolution (group-resolution resolutions group)]
                         [sortable-table/table
                          {:tr            tr
                           :items         records
                           :id-func       :property/integration-id
                           :cols          [[:property/integration-id
                                            :property/integration-id
                                            {:width "12rem"}]
                                           [:address/full-address
                                            :address/full-address
                                            {:flex 2}]
                                           [:property-gis-import/import-choice
                                            (partial record-import-choice
                                                     (util/mmap tr multiple-addresses? disabled?)
                                                     group
                                                     resolution)
                                            {:width "16rem"}]]
                           :expanded-func (partial gis-fields tr)}]))}]))

(defn gis-mapping-section [tr]
  (let [open?        (<sub [::mapping-modal-open?])
        mapping      (<sub [::gis-mapping-no-attributes])
        attr-mapping (<sub [::property-attribute-gis-mapping])]
    (when (and open? (seq mapping))
      [ui-modal/composed-input-modal
       {:open    open?
        :data-cy "gis-mapping-modal"
        :key     "gis-mapping-modal"}
       (cc/modal-header
         {:button-on-click #(>evt [::set-mapping-modal-open? false])}
         [:h3 (tr [:property-gis-import/gis-mapping-title])])
       (cc/modal-body
         [:div
          (when (seq attr-mapping)
            [:div.mb-8
             [ui-table/modern-responsive-table
              {:tr                tr
               :items             attr-mapping
               :empty-placeholder (tr [:property-gis-import/no-records])
               :id-func           first
               :cols              [[:property-gis-import/property-attribute-name
                                    first]
                                   [:property-gis-import/gis-field
                                    second]]}]])
          [ui-table/modern-responsive-table
           {:tr                tr
            :items             mapping
            :empty-placeholder (tr [:property-gis-import/no-records])
            :id-func           first
            :cols              [[:property-gis-import/property-field
                                 (comp tr vector first)]
                                [:property-gis-import/gis-field
                                 second]]}]])
       (cc/modal-footer
         {:secondary-button-text (tr [:close])
          :on-request-close      #(>evt [::set-mapping-modal-open? false])})])))
